import { z } from 'zod';
import { EnteredNameSchema, RecordIdSchema } from './identifiers';
import { QuantitySchema } from './invoicing';
import { DecimalStringSchema, MoneySchema } from './money';
import { RetentionTermsSchema } from './procurement';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Construction and contractors — BMP-2 package 4b (`CONST-CONTRACT-001`, `CONST-CERT-001`,
 * `CONST-PROGRESS-001`).
 *
 * A **contractor contract** is held with a vendor of kind contractor, for one project, with a bill of
 * quantities (BOQ). A subcontract names its main contract (`G-13`). Advance, retention and their
 * recovery are entered per contract (`BD-51` — no default). Variations (change orders) add or change
 * BOQ lines as numbered, approved revisions; history is never rewritten.
 *
 * A **progress certificate** states, per BOQ line, the cumulative quantity executed to date. The
 * period's quantity is the difference from the previous certificate, so a figure is never counted
 * twice and never above the contracted quantity. Gross, retention, advance recovery and other
 * deductions are exact decimals; the net is what becomes payable. A certificate is approved by
 * engineering, then by finance — two different people, neither its preparer — and only then is it
 * certified, reported to the posting engine (`construction.certified`) and payable through a treasury
 * payment request. It is **paid** only when treasury executes that payment.
 */

export const CONTRACT_KINDS = ['contract', 'subcontract'] as const;
export const ContractKindSchema = z.enum(CONTRACT_KINDS);

export const CONSTRUCTION_CONTRACT_STATES = [
  'draft',
  'pendingApproval',
  'active',
  'completed',
  'closed',
  'cancelled',
  'rejected',
] as const;
export const ConstructionContractStateSchema = z.enum(CONSTRUCTION_CONTRACT_STATES);
export type ConstructionContractState = z.infer<typeof ConstructionContractStateSchema>;

const RateSchema = DecimalStringSchema.refine(
  (value) => !value.startsWith('-') && !/\.\d{5,}$/.test(value),
  { message: 'RATE_EXPECTED' },
);
const PercentSchema = z
  .string()
  .regex(/^\d{1,2}(\.\d{1,4})?$/, { message: 'PERCENT_EXPECTED' })
  .refine((value) => Number(value) > 0 && Number(value) < 100, { message: 'PERCENT_EXPECTED' });

export const BoqLineInputSchema = z.strictObject({
  code: z.string().trim().min(1).max(30),
  description: z.string().trim().min(2).max(300),
  unit: z.string().trim().min(1).max(20),
  quantity: QuantitySchema,
  unitRate: RateSchema,
});
export type BoqLineInput = z.infer<typeof BoqLineInputSchema>;

export const BoqLineSchema = BoqLineInputSchema.extend({
  lineNo: z.number().int().positive(),
  amount: DecimalStringSchema,
  /** Cumulative quantity on the latest certified certificate. */
  certifiedQuantity: DecimalStringSchema,
  /** The variation that added or last changed the line, when one did. */
  variationNo: z.number().int().positive().optional(),
});
export type BoqLine = z.infer<typeof BoqLineSchema>;

export const GUARANTEE_KINDS = ['performance', 'advancePayment', 'retention', 'other'] as const;
export const GuaranteeKindSchema = z.enum(GUARANTEE_KINDS);
export const GUARANTEE_STATES = ['active', 'released', 'called'] as const;

export const GuaranteeSchema = z.strictObject({
  guaranteeId: RecordIdSchema,
  kind: GuaranteeKindSchema,
  /** The bank guarantee or bond number, as printed. */
  reference: z.string(),
  issuer: z.string(),
  amount: MoneySchema,
  expiresOn: BusinessDateSchema,
  state: z.enum(GUARANTEE_STATES),
  note: z.string().optional(),
});
export type Guarantee = z.infer<typeof GuaranteeSchema>;

export const GuaranteeInputSchema = z.strictObject({
  kind: GuaranteeKindSchema,
  reference: z.string().trim().min(1).max(80),
  issuer: z.string().trim().min(2).max(160),
  amount: MoneySchema,
  expiresOn: BusinessDateSchema,
  note: z.string().trim().max(300).optional(),
});

export const AdvanceTermsSchema = z.strictObject({
  amount: MoneySchema,
  /** Recovered from each certificate as this percentage of its gross, until fully recovered. */
  recoveryPercent: PercentSchema,
});
export type AdvanceTerms = z.infer<typeof AdvanceTermsSchema>;

export const ConstructionContractSchema = z.strictObject({
  contractId: RecordIdSchema,
  number: z.string(),
  kind: ContractKindSchema,
  parentContractId: RecordIdSchema.optional(),
  vendorId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema,
  title: EnteredNameSchema,
  /** The project phase or work package the contract's cost belongs to (FIN-REPORT-005); free text. */
  phase: z.string().optional(),
  scope: z.string().optional(),
  currency: z.string(),
  startsOn: BusinessDateSchema.optional(),
  endsOn: BusinessDateSchema.optional(),
  boq: z.array(BoqLineSchema),
  total: MoneySchema,
  retention: RetentionTermsSchema.optional(),
  advance: AdvanceTermsSchema.optional(),
  /** What treasury paid of the advance, and what certificates recovered of it. */
  advancePaid: MoneySchema,
  advanceRecovered: MoneySchema,
  /** Retention held by certified certificates, and released since. */
  retentionHeld: MoneySchema,
  retentionReleased: MoneySchema,
  guarantees: z.array(GuaranteeSchema),
  variationCount: z.number().int().nonnegative(),
  state: ConstructionContractStateSchema,
  approvalRequestId: z.string().optional(),
  createdByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type ConstructionContract = z.infer<typeof ConstructionContractSchema>;

export const CreateConstructionContractSchema = z
  .strictObject({
    kind: ContractKindSchema,
    parentContractId: RecordIdSchema.optional(),
    vendorId: RecordIdSchema,
    branchId: RecordIdSchema,
    projectId: RecordIdSchema,
    title: EnteredNameSchema,
    scope: z.string().trim().max(2000).optional(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    startsOn: BusinessDateSchema.optional(),
    endsOn: BusinessDateSchema.optional(),
    boq: z.array(BoqLineInputSchema).min(1).max(500),
    retention: RetentionTermsSchema.optional(),
    advance: AdvanceTermsSchema.optional(),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .refine((value) => value.kind === 'contract' || !!value.parentContractId, {
    message: 'MAIN_CONTRACT_REQUIRED',
    path: ['parentContractId'],
  })
  .refine((value) => new Set(value.boq.map((line) => line.code)).size === value.boq.length, {
    message: 'BOQ_CODE_DUPLICATE',
    path: ['boq'],
  });
export type CreateConstructionContract = z.infer<typeof CreateConstructionContractSchema>;

export const ContractActionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500).optional(),
});

/** A variation (change order): lines added, or the quantity or rate of existing lines changed. */
export const VariationSchema = z.strictObject({
  variationNo: z.number().int().positive(),
  reason: z.string(),
  added: z.array(BoqLineInputSchema),
  changed: z.array(
    z.strictObject({
      lineNo: z.number().int().positive(),
      quantity: DecimalStringSchema,
      unitRate: DecimalStringSchema,
      previousQuantity: DecimalStringSchema,
      previousRate: DecimalStringSchema,
    }),
  ),
  /** The change of contract value this variation makes (positive or negative). */
  delta: MoneySchema,
  state: z.enum(['pendingApproval', 'approved', 'rejected']),
  approvalRequestId: z.string().optional(),
  requestedByAccountId: z.string(),
  requestedAt: InstantSchema,
});
export type Variation = z.infer<typeof VariationSchema>;

export const RequestVariationSchema = z
  .strictObject({
    reason: z.string().trim().min(3).max(500),
    added: z.array(BoqLineInputSchema).max(200).default([]),
    changed: z
      .array(
        z.strictObject({
          lineNo: z.number().int().positive(),
          quantity: QuantitySchema,
          unitRate: RateSchema,
        }),
      )
      .max(200)
      .default([]),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => value.added.length + value.changed.length > 0, {
    message: 'VARIATION_EMPTY',
    path: ['added'],
  });

export const AddGuaranteeSchema = GuaranteeInputSchema.extend({
  expectedVersion: z.number().int().positive(),
});

export const GuaranteeActionSchema = z.strictObject({
  action: z.enum(['release', 'call']),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const ContractPaymentSchema = z.strictObject({
  /** What is paid: the advance, released retention, or a certified certificate. */
  purpose: z.enum(['advance', 'retention']),
  amount: MoneySchema,
  fromAccountId: RecordIdSchema,
  method: z.enum(['bankTransfer', 'cash', 'cheque']),
  idempotencyKey: z.string().trim().min(8).max(200),
});

export const ConstructionQuerySchema = z.strictObject({
  projectId: RecordIdSchema.optional(),
  vendorId: RecordIdSchema.optional(),
  state: ConstructionContractStateSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type ConstructionQuery = z.infer<typeof ConstructionQuerySchema>;

/* ----------------------------------------------------------- certificates */

export const CERTIFICATE_STATES = [
  'draft',
  'submitted',
  'engineeringApproved',
  'certified',
  'partiallyPaid',
  'paid',
  'rejected',
  'cancelled',
] as const;
export const CertificateStateSchema = z.enum(CERTIFICATE_STATES);
export type CertificateState = z.infer<typeof CertificateStateSchema>;

export const CertificateLineSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  code: z.string(),
  description: z.string(),
  unit: z.string(),
  unitRate: DecimalStringSchema,
  contractQuantity: DecimalStringSchema,
  previousQuantity: DecimalStringSchema,
  cumulativeQuantity: DecimalStringSchema,
  periodQuantity: DecimalStringSchema,
  periodAmount: DecimalStringSchema,
});
export type CertificateLine = z.infer<typeof CertificateLineSchema>;

export const ProgressCertificateSchema = z.strictObject({
  certificateId: RecordIdSchema,
  number: z.string(),
  sequence: z.number().int().positive(),
  contractId: RecordIdSchema,
  vendorId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema,
  periodFrom: BusinessDateSchema,
  periodTo: BusinessDateSchema,
  lines: z.array(CertificateLineSchema),
  gross: MoneySchema,
  retention: MoneySchema,
  advanceRecovery: MoneySchema,
  otherDeductions: z.array(z.strictObject({ description: z.string(), amount: MoneySchema })),
  deductions: MoneySchema,
  net: MoneySchema,
  paid: MoneySchema,
  /** On payment requests not yet executed, rejected or cancelled. */
  requested: MoneySchema,
  state: CertificateStateSchema,
  preparedByAccountId: z.string(),
  engineeringApprovedByAccountId: z.string().optional(),
  financeApprovedByAccountId: z.string().optional(),
  approvalRequestId: z.string().optional(),
  rejection: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type ProgressCertificate = z.infer<typeof ProgressCertificateSchema>;

export const PrepareCertificateSchema = z.strictObject({
  periodFrom: BusinessDateSchema,
  periodTo: BusinessDateSchema,
  /** Cumulative quantity executed to date, per BOQ line; lines left out keep the previous figure. */
  lines: z
    .array(
      z.strictObject({
        lineNo: z.number().int().positive(),
        cumulativeQuantity: DecimalStringSchema.refine((value) => !value.startsWith('-'), {
          message: 'QUANTITY_EXPECTED',
        }),
      }),
    )
    .min(1)
    .max(500),
  otherDeductions: z
    .array(
      z.strictObject({
        description: z.string().trim().min(2).max(200),
        amount: MoneySchema,
      }),
    )
    .max(20)
    .default([]),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type PrepareCertificate = z.infer<typeof PrepareCertificateSchema>;

export const CertificateActionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
  reason: z.string().trim().min(3).max(500).optional(),
});

export const CertificatePaymentSchema = z.strictObject({
  amount: MoneySchema,
  fromAccountId: RecordIdSchema,
  method: z.enum(['bankTransfer', 'cash', 'cheque']),
  idempotencyKey: z.string().trim().min(8).max(200),
});

/* ------------------------------------------------------------- progress */

export const MILESTONE_STATES = ['planned', 'inProgress', 'achieved', 'delayed'] as const;
export const PROGRESS_ENTRY_KINDS = ['milestone', 'dailyReport', 'delay', 'risk'] as const;
export const ProgressEntryKindSchema = z.enum(PROGRESS_ENTRY_KINDS);
export const PROGRESS_RISK_LEVELS = ['low', 'medium', 'high'] as const;

/**
 * The project progress register (CONST-PROGRESS-001): milestones with planned and actual dates, daily
 * site reports, delays and risks. Entries are appended and closed; never edited away. Photos and
 * documents attach to the project through the documents module.
 */
export const ProgressEntrySchema = z.strictObject({
  entryId: RecordIdSchema,
  kind: ProgressEntryKindSchema,
  projectId: RecordIdSchema,
  contractId: RecordIdSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  title: z.string(),
  description: z.string().optional(),
  /** Milestone: planned date. Daily report: the day. Delay and risk: when raised. */
  date: BusinessDateSchema,
  /** Milestone only. */
  plannedOn: BusinessDateSchema.optional(),
  achievedOn: BusinessDateSchema.optional(),
  milestoneState: z.enum(MILESTONE_STATES).optional(),
  /** Daily report only. */
  manpower: z.number().int().nonnegative().optional(),
  weather: z.string().optional(),
  /** Delay only: days of impact claimed or estimated. */
  impactDays: z.number().int().nonnegative().optional(),
  /** Risk only. */
  level: z.enum(PROGRESS_RISK_LEVELS).optional(),
  mitigation: z.string().optional(),
  open: z.boolean(),
  closedReason: z.string().optional(),
  recordedByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type ProgressEntry = z.infer<typeof ProgressEntrySchema>;

export const RecordProgressSchema = z
  .strictObject({
    kind: ProgressEntryKindSchema,
    projectId: RecordIdSchema,
    contractId: RecordIdSchema.optional(),
    title: z.string().trim().min(2).max(200),
    description: z.string().trim().max(4000).optional(),
    date: BusinessDateSchema,
    plannedOn: BusinessDateSchema.optional(),
    manpower: z.number().int().nonnegative().max(100_000).optional(),
    weather: z.string().trim().max(60).optional(),
    impactDays: z.number().int().nonnegative().max(10_000).optional(),
    level: z.enum(PROGRESS_RISK_LEVELS).optional(),
    mitigation: z.string().trim().max(2000).optional(),
    idempotencyKey: z.string().trim().min(8).max(200),
  })
  .refine((value) => value.kind !== 'milestone' || !!value.plannedOn, {
    message: 'PLANNED_DATE_REQUIRED',
    path: ['plannedOn'],
  })
  .refine((value) => value.kind !== 'risk' || !!value.level, {
    message: 'RISK_LEVEL_REQUIRED',
    path: ['level'],
  });
export type RecordProgress = z.infer<typeof RecordProgressSchema>;

export const UpdateProgressSchema = z.strictObject({
  milestoneState: z.enum(MILESTONE_STATES).optional(),
  achievedOn: BusinessDateSchema.optional(),
  close: z.boolean().optional(),
  reason: z.string().trim().min(3).max(500).optional(),
  expectedVersion: z.number().int().positive(),
});

export const ProgressQuerySchema = z.strictObject({
  projectId: RecordIdSchema,
  kind: ProgressEntryKindSchema.optional(),
  open: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

/** Project cost from what is known: certified contractor work and recorded vendor invoices. */
export const ProjectCostSchema = z.strictObject({
  projectId: RecordIdSchema,
  currency: z.string(),
  contracts: z.array(
    z.strictObject({
      contractId: RecordIdSchema,
      number: z.string(),
      title: z.string(),
      total: MoneySchema,
      certified: MoneySchema,
      paid: MoneySchema,
      progressPercent: z.string().optional(),
    }),
  ),
  contractValue: MoneySchema,
  certifiedToDate: MoneySchema,
  vendorInvoices: MoneySchema,
  /**
   * Sales of the project's units (active, completed contracts) — present only for a reader who may see
   * contracts. Margin = contracted − (contract value of construction + vendor invoices): partial, as
   * materials are not valued (BD-67) and indirect costs are not allocated.
   */
  revenue: z
    .strictObject({ contracted: MoneySchema, collected: MoneySchema, outstanding: MoneySchema })
    .optional(),
  margin: MoneySchema.optional(),
  /** Quantities of materials issued to the project; not valued until BD-67 is decided. */
  materialIssues: z.number().int().nonnegative(),
  /** Always partial: materials are not valued and posted actuals depend on the posting map. */
  partial: z.literal(true),
  generatedAt: InstantSchema,
});
export type ProjectCost = z.infer<typeof ProjectCostSchema>;

/* -------------------------------------------- project profitability (FIN-REPORT-005) */

/** A project phase or work package label; absent means "not assigned to a phase". */
export const ProjectPhaseSchema = z.string().trim().min(1).max(80);

export const SetContractPhaseSchema = z.strictObject({
  phase: ProjectPhaseSchema.nullable(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

/**
 * How sales revenue is recognized in project profitability (`BD-71`). Not configured: no profit is
 * shown — contracted value and collected cash are shown as what they are. Each choice is explained on
 * the dashboard and adopted explicitly, with a reason, by an authorized person.
 */
export const REVENUE_RECOGNITION_RULES = ['onContract', 'onHandover', 'onCollection'] as const;
export const RevenueRecognitionSchema = z.enum(REVENUE_RECOGNITION_RULES).nullable();
export type RevenueRecognition = z.infer<typeof RevenueRecognitionSchema>;

export const PROJECT_COST_SOURCES = [
  'constructionContract',
  'progressCertificate',
  'purchaseOrder',
  'vendorInvoice',
  'expense',
  /** Materials issued to the project, valued at weighted average once BD-67 is adopted. */
  'stockIssue',
] as const;

/** One record behind a phase's figure — the drill-down to the source transaction. */
export const ProjectCostSourceRowSchema = z.strictObject({
  source: z.enum(PROJECT_COST_SOURCES),
  id: RecordIdSchema,
  number: z.string(),
  date: BusinessDateSchema.optional(),
  /** `committed`: promised, not yet incurred; `actual`: incurred (certified, invoiced, approved). */
  kind: z.enum(['committed', 'actual']),
  amount: MoneySchema,
});

export type ProjectCostSourceRow = z.infer<typeof ProjectCostSourceRowSchema>;

export const ProjectProfitabilitySchema = z.strictObject({
  projectId: RecordIdSchema,
  currency: z.string(),
  revenue: z
    .strictObject({
      /** Value of active and completed unit sales contracts. */
      contracted: MoneySchema,
      /** Cash collected on them. */
      collected: MoneySchema,
      /** Under the adopted rule only; absent while `BD-71` is not configured. */
      recognized: MoneySchema.optional(),
    })
    .optional(),
  /**
   * The rule the report applies. Recognition is a report figure only — nothing is posted to the ledger
   * under it — so changing the rule changes no posted record; the report states the rule it used.
   */
  recognition: z.strictObject({
    rule: RevenueRecognitionSchema,
    decision: z.literal('BD-71'),
    /** The setting revision in force and when it was set; absent while not adopted. */
    version: z.number().int().positive().optional(),
    adoptedAt: InstantSchema.optional(),
  }),
  phases: z.array(
    z.strictObject({
      phase: z.string().nullable(),
      committed: MoneySchema,
      actual: MoneySchema,
      sources: z.array(ProjectCostSourceRowSchema),
    }),
  ),
  totals: z.strictObject({ committed: MoneySchema, actual: MoneySchema }),
  /** Recognized revenue − actual cost; absent while the recognition rule is not adopted. */
  profit: MoneySchema.optional(),
  /** Cost sources that exist and are not in the figures, each with why — never dropped silently. */
  notIncluded: z.array(
    z.strictObject({
      source: z.enum(['materials', 'payroll', 'indirectCosts', 'otherCurrency']),
      reason: z.string(),
    }),
  ),
  generatedAt: InstantSchema,
});
export type ProjectProfitability = z.infer<typeof ProjectProfitabilitySchema>;

/* -------------------------------------------------------- approvals, audit */

export const CONSTRUCTION_APPROVAL_OPERATIONS = {
  /** A contract: active at once unless a policy governs it. */
  contract: 'construction.contract.approve',
  /** A variation (change order): applied at once unless a policy governs it. */
  variation: 'construction.variation.approve',
  /** A certificate's finance approval through the engine where a policy governs it. */
  certificate: 'construction.certificate.approve',
} as const;

export const CONSTRUCTION_AUDIT_ACTIONS = {
  contractCreated: 'construction.contract.created',
  contractStateChanged: 'construction.contract.stateChanged',
  contractPhaseSet: 'construction.contract.phaseSet',
  variationRequested: 'construction.variation.requested',
  variationDecided: 'construction.variation.decided',
  guaranteeAdded: 'construction.guarantee.added',
  guaranteeChanged: 'construction.guarantee.changed',
  paymentRequested: 'construction.payment.requested',
  paymentSettled: 'construction.payment.settled',
  certificatePrepared: 'construction.certificate.prepared',
  certificateStateChanged: 'construction.certificate.stateChanged',
  progressRecorded: 'construction.progress.recorded',
  progressUpdated: 'construction.progress.updated',
} as const;
